import { useState, type ReactNode } from "react";
import { format, parseISO } from "date-fns";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  Wrench,
  Clock,
  Activity,
  CalendarClock,
  Boxes,
  ShieldCheck,
  DollarSign,
  MessageSquare,
  Sparkles,
  Megaphone,
  Pencil,
  Plus,
  RefreshCw,
  FolderOpen,
  SlidersHorizontal,
  GripVertical,
  ArrowUp,
  ArrowDown,
  ArrowLeftRight,
  RotateCcw,
  Check,
  Receipt,
  type LucideIcon,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/Card";
import { CardHeader } from "@/components/ui/CardHeader";
import { TechAvatar } from "@/components/TechAvatar";
import { TechLink } from "@/components/links";
import {
  extractJobPreview,
  resolveWindow,
  resolveWindowAndPriority,
  formatTime,
} from "@/lib/format";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { WindowBadge } from "@/components/ui/WindowBadge";
import { normalizeStatus } from "@/lib/status";
import { cn } from "@/lib/cn";
import { useCustomerDispatches, type CustomerDispatchRow } from "./api";
import { CustomerPendingTickets } from "./CustomerPendingTickets";
import { CustomerFiles } from "./CustomerFiles";
import { RecommendationsCard } from "@/features/recommendations/RecommendationsCard";
import { AttachFromCallCard } from "@/features/calls/AttachFromCallCard";
import { CustomerScheduledJobs } from "./CustomerScheduledJobs";
import { CustomerPropertyHistory } from "./CustomerPropertyHistory";
import { DispatchPeek } from "./DispatchPeek";
import { FollowUpsCard } from "@/features/followups/FollowUpsCard";
import { FrayedAction } from "./FrayedAction";
import {
  useLeadSourceOptions,
  useCustomerLeadSource,
  useSetCustomerLeadOverride,
  useAddLeadSourceOption,
} from "./leadSource";
import { useCustomerEquipment, useAiEquipmentOverrides } from "./CustomerEquipment";
import { buildEquipmentView, installDateLabel } from "@/lib/equipment";
import { equipStyle } from "@/lib/equipmentIcons";
import { useCustomerMetrics, formatCentsAsHuman } from "./metrics";
import { useMembershipCard } from "@/features/agreements/api";
import { AllPropertiesChip } from "./ScopeChip";
import { onErrToast } from "@/components/ui/mutationToast";
import { useLocationNotesSummary } from "./useLocationNotesSummary";
import { useCustomerFollowups } from "@/features/followups/followups";
import { useRecommendations } from "@/features/recommendations/recommendations";
import { useCustomerFiles } from "@/features/files/filesCore";
import { useMessages } from "./CustomerMessages";
import { useVisitHeadlines, type VisitHeadline } from "./visitSummary";
import { WatchOutsBlock } from "./WatchOutsBlock";
import { PeopleBlock } from "./CustomerRail";
import { useLocationLifetimes, useSnapshotNote, useSaveSnapshotNote } from "./api";
import { Copyable } from "@/components/Copyable";
import { StreetViewThumb } from "./CustomerAvatar";
import { useOwnerEra } from "./ownerEra";
import { displayCustomerName } from "@/lib/format";
import { useCustomerStatusFlag, isDoNotService, type CustomerWithLocations, type LocationRow } from "./api";
import { useEditCustomer } from "./editCustomerContext";
import { ProposalsOverviewCard } from "./CustomerProposals";

// ============================================================================
// CustomerOverview — the curated default tab of Customer 360 (BUILD-13c).
//
// Top:  ADAPTIVE DISPATCH BANNER (2026-05-28) — surfaces the one dispatch the
//       dispatcher most needs right now: an ACTIVE job (tech working/traveling)
//       takes priority, else the NEXT upcoming/scheduled visit. Per Seth, the
//       time window + special code (PAIR / TECH / PART / etc.) are foregrounded
//       here because on a LIVE/UPCOMING job that's what the dispatcher needs at
//       a glance — unlike history rows, where they stay lighter. Self-hides
//       when there's nothing active or upcoming (empty-slot rule).
//
// Below: RECENT SERVICE (last 3 + "View all" → Service tab).
//
// Other mockup boxes (Equipment / Agreements / Recommendations / AI Insights)
// self-appear here as their data sources land (MIR-02 / MIR-03 / CRM-002).
// ============================================================================

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ============================================================================
// C360-CUSTOM-01 (Seth 2026-10-01: "can we make the 360 layout customizable?
// …rearrange where they want their boxes… remember their layout based on
// login" → "go with your recommendations"): card REORDER per login — move up
// / down / across the two columns, saved to pegasus_user_pref (RLS: each
// login sees only its own), one-click Reset to standard. Deliberately NO
// free resize: the even 50/50 grid is what keeps the overview readable, and
// a shared "standard layout" keeps training and phone-support language
// common (HubSpot allows card reorder; ServiceTitan/Jobber ship fixed;
// nobody hands end users free resize on a record page).
// ============================================================================

type OverviewColumn = "left" | "right";
type OverviewLayout = Record<OverviewColumn, string[]>;

const LAYOUT_KEY = "c360.overview.v1";

/** The standard layout — DISPATCHER-FIRST-01's shape. New cards get added
 *  here and appear for everyone at their standard spot. */
const LAYOUT_DEFAULT: OverviewLayout = {
  left: ["people", "watchfor", "snapshot", "scheduled", "pending", "proposals", "proposed"],
  right: ["address", "timeline", "recent", "messages", "records"],
};

const CARD_LABEL: Record<string, string> = {
  people: "People",
  watchfor: "Watch For",
  snapshot: "Pegasus Snapshot",
  scheduled: "Scheduled Jobs",
  pending: "Pending Tickets",
  proposals: "Proposals & Quotes",
  proposed: "Proposed Work & Follow-Ups",
  address: "At This Address",
  timeline: "Property Timeline",
  recent: "Recent Service",
  messages: "Messages & Touchpoints",
  records: "Records",
};

/** Saved order, normalized: unknown ids drop, missing ids rejoin at their
 *  standard column's end — so adding a card in code never strands a saved
 *  layout. */
function normalizeLayout(saved: OverviewLayout | null): OverviewLayout {
  if (!saved) return LAYOUT_DEFAULT;
  const known = new Set([...LAYOUT_DEFAULT.left, ...LAYOUT_DEFAULT.right]);
  const seen = new Set<string>();
  const take = (ids: unknown): string[] =>
    (Array.isArray(ids) ? ids : []).filter((id): id is string => {
      if (typeof id !== "string" || !known.has(id) || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  const left = take(saved.left);
  const right = take(saved.right);
  for (const col of ["left", "right"] as const) {
    for (const id of LAYOUT_DEFAULT[col]) if (!seen.has(id)) { seen.add(id); (col === "left" ? left : right).push(id); }
  }
  return { left, right };
}

function useOverviewLayout() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["user_pref", LAYOUT_KEY],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<OverviewLayout | null> => {
      const { data, error } = await supabase
        .from("pegasus_user_pref")
        .select("value")
        .eq("key", LAYOUT_KEY)
        .maybeSingle();
      if (error) throw error;
      return ((data as { value?: OverviewLayout } | null)?.value ?? null);
    },
  });
  const save = useMutation({
    mutationFn: async (v: OverviewLayout | null) => {
      if (v === null) {
        const { error } = await supabase.from("pegasus_user_pref").delete().eq("key", LAYOUT_KEY);
        if (error) throw error;
        return;
      }
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_user_pref").upsert({
        user_id: u?.user?.id,
        key: LAYOUT_KEY,
        value: v,
        updated_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      qc.setQueryData(["user_pref", LAYOUT_KEY], v);
      void qc.invalidateQueries({ queryKey: ["user_pref", LAYOUT_KEY] });
    },
  });
  return { saved: q.data ?? null, save };
}

interface Props {
  custno: string;
  locno?: string | null;
  /** 360-AUDIT wave 2 (B): whole-account cards say so on multi-property accounts. */
  multiProperty?: boolean;
  onViewAllService: () => void;
  /** C360-BOARD-01: the board overview needs the full objects — the feed
   *  column carries Notes, the rail carries Watch-outs + People. */
  customer?: CustomerWithLocations;
  selectedLocation?: LocationRow | null;
  onOpenEquipment?: () => void;
}

export function CustomerOverview({ custno, locno, multiProperty, onViewAllService, customer, selectedLocation, onOpenEquipment }: Props) {
  const { data, isLoading, isError } = useCustomerDispatches(custno);
  const rows = (data ?? []).filter((r) => (locno ? r.locno === locno : true));
  // SERVICE-ONELINE-01: one clean line per visit (AI when it exists) + billed.
  const { headlineFor } = useVisitHeadlines(custno, rows.slice(0, 5));
  const recent = rows.slice(0, 5);
  // Per-owner attribution for rows dated before an ownership handoff.
  const ownerEra = useOwnerEra(custno, locno ?? null);
  // FLOW-01: a clicked dispatch PEEKS in from the right — the dispatcher
  // stays on the customer page (two-window model).
  const [peekDispatch, setPeekDispatch] = useState<string | null>(null);
  // --- Adaptive banner selection: active wins, else soonest upcoming -------
  const today = todayISO();
  const activeRow = rows.find((r) => {
    const k = normalizeStatus(r.status);
    return k === "working" || k === "traveling";
  });
  const upcomingRow = rows
    .filter(
      (r) =>
        !!r.appointment_date &&
        r.appointment_date >= today &&
        normalizeStatus(r.status) !== "complete",
    )
    .sort((a, b) => (a.appointment_date ?? "").localeCompare(b.appointment_date ?? ""))[0];
  const bannerRow = activeRow ?? upcomingRow ?? null;
  const bannerKind: "active" | "upcoming" | null = activeRow
    ? "active"
    : upcomingRow
      ? "upcoming"
      : null;

  // STRIP+A (Seth 2026-08-21: "Strip + A for sure") — empty cards COLLAPSE.
  // Recommendations + Follow-ups render full cards only when they hold work
  // (or the office just clicked "+"); otherwise one quiet line keeps their
  // add-affordances alive without spending two card headers on nothing.
  // (Scheduled + Pending already self-hide when empty.) React Query dedupes
  // these reads with the cards' own — no extra fetches.
  const openRecs = (useRecommendations(custno).data ?? []).filter((r) => r.status === "open").length;
  const openFollowups = (useCustomerFollowups(custno).data ?? []).length;
  const [forceRecs, setForceRecs] = useState(false);
  const [forceFollowups, setForceFollowups] = useState(false);
  const showRecs = openRecs > 0 || forceRecs;
  const showFollowups = openFollowups > 0 || forceFollowups;

  // 360-HD: signals stand alone below the snapshot now (Seth 2026-09-01) —
  // no more riding inside it, so no snapshot pre-read needed here.

  // C360-CUSTOM-01: this login's card order (standard until they change it).
  const { saved, save: saveLayout } = useOverviewLayout();
  const layout = normalizeLayout(saved);
  const [customizing, setCustomizing] = useState(false);
  const moveCard = (col: OverviewColumn, idx: number, dir: "up" | "down" | "across") => {
    const next: OverviewLayout = { left: [...layout.left], right: [...layout.right] };
    const [id] = next[col].splice(idx, 1);
    if (!id) return;
    if (dir === "across") {
      const other: OverviewColumn = col === "left" ? "right" : "left";
      next[other].splice(Math.min(idx, next[other].length), 0, id);
    } else {
      next[col].splice(dir === "up" ? Math.max(0, idx - 1) : Math.min(next[col].length, idx + 1), 0, id);
    }
    saveLayout.mutate(next);
  };

  // The card registry — each id renders its DISPATCHER-FIRST-01 block.
  // A card that has nothing to show renders null (self-hiding rule), and in
  // customize mode a dashed stub stands in so it can still be arranged.
  function renderCard(id: string): ReactNode {
    switch (id) {
      case "people":
        // WHO might be on the line — names + labels first (3300 Corporate:
        // Chris/Jim · Chris · Randy · Julie). The header's chip jumps here.
        return customer ? (
          <Card id="sub-people" className="scroll-mt-24 overflow-hidden">
            <PeopleBlock customer={customer} location={selectedLocation ?? null} />
          </Card>
        ) : null;
      case "watchfor":
        // What to know BEFORE you speak — gate codes, dogs, DNS, COD.
        return customer ? <GoodToKnowRailCard customer={customer} locno={locno ?? null} /> : null;
      case "snapshot":
        return <AiSnapshotCard custno={custno} locno={locno} />;
      case "scheduled":
        // The customer's waiting work — when they call, this is usually why.
        return <CustomerScheduledJobs custno={custno} />;
      case "pending":
        return <CustomerPendingTickets custno={custno} />;
      case "proposals":
        // QUOTE-POLISH-01 (Seth: "finding quotes is difficult"): open proposals
        // + the Build buttons, right on the Overview — no new nav pill.
        return <ProposalsOverviewCard custno={custno} locno={locno ?? null} onViewAll={onViewAllService} />;
      case "proposed":
        return (
          <>
            {showRecs && <RecommendationsCard custno={custno} locno={locno} startAdding={forceRecs && openRecs === 0} />}
            {showFollowups && <FollowUpsCard custno={custno} locno={locno} startAdding={forceFollowups && openFollowups === 0} />}
            {(!showRecs || !showFollowups) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-dashed border-hairline bg-slate-50/50 px-3.5 py-2 text-xs text-slate-400">
                <span>
                  {!showRecs && !showFollowups
                    ? "Nothing waiting."
                    : !showFollowups
                      ? "No follow-ups."
                      : "No proposed work."}
                </span>
                <span className="ml-auto flex items-center gap-3">
                  {!showFollowups && (
                    <button type="button" onClick={() => setForceFollowups(true)}
                      className="font-medium text-brand-600 transition-colors hover:text-brand-700">
                      + Follow-up
                    </button>
                  )}
                  {!showRecs && (
                    <button type="button" onClick={() => setForceRecs(true)}
                      className="font-medium text-brand-600 transition-colors hover:text-brand-700">
                      + Recommend Work
                    </button>
                  )}
                </span>
              </div>
            )}
          </>
        );
      case "address":
        // The board's keeper: property photo + the systems behind that front
        // door — with this property's lifetime $ (PROP-LTV-01) and the
        // BILLS TO band when the account bills elsewhere (BILL-TO-VIS-01).
        return <AtThisAddressCard custno={custno} loc={selectedLocation ?? null} onOpenEquipment={onOpenEquipment} multiProperty={multiProperty} customer={customer} />;
      case "timeline":
        return <CustomerPropertyHistory custno={custno} locno={locno ?? null} multiProperty={multiProperty} />;
      case "recent":
        return (
          <Card className="overflow-hidden">
            <CardHeader icon={Wrench} title="Recent Service" count={rows.length > 0 ? rows.length : null} action={rows.length > 0 ? { label: "View all", onClick: onViewAllService, arrow: true } : undefined} />
            {isLoading ? (
              <div className="space-y-px">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-14 animate-pulse bg-slate-50" />
                ))}
              </div>
            ) : isError ? (
              <div className="p-6"><CardError what="service history" /></div>
            ) : recent.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">
                No service history for this {locno ? "location" : "customer"} yet.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {recent.map((r, i) => (
                  <RecentServiceRow key={`${r.dispatch}-${r.tech_empno}-${i}`} row={r}
                    headline={headlineFor(r)}
                    onPeek={setPeekDispatch}
                    priorOwner={ownerEra.priorOwnerFor(r.appointment_date)} />
                ))}
              </ul>
            )}
          </Card>
        );
      case "messages":
        return <MessagesSummaryCard custno={custno} />;
      case "records":
        return <RecordGroup custno={custno} locno={locno} />;
      default:
        return null;
    }
  }

  // A column slot: in customize mode every card wears a move bar (↑ ↓ ⇄),
  // and self-hidden cards show a dashed stub so they can still be placed.
  function renderSlot(id: string, col: OverviewColumn, idx: number): ReactNode {
    const inner = renderCard(id);
    if (!customizing) return inner ? <div key={id}>{inner}</div> : null;
    const last = layout[col].length - 1;
    const ctl = "rounded-md p-1 text-brand-600 transition-colors hover:bg-brand-100 disabled:opacity-30 disabled:hover:bg-transparent";
    return (
      <div key={id}>
        <div className="mb-1 flex items-center gap-1 rounded-lg border border-dashed border-brand-300 bg-brand-50/70 px-2 py-1">
          <GripVertical className="h-3.5 w-3.5 shrink-0 text-brand-400" />
          <span className="min-w-0 flex-1 truncate text-[11px] font-bold text-brand-800">{CARD_LABEL[id] ?? id}</span>
          <button type="button" disabled={idx === 0} onClick={() => moveCard(col, idx, "up")} title="Move up" className={ctl}><ArrowUp className="h-3.5 w-3.5" /></button>
          <button type="button" disabled={idx === last} onClick={() => moveCard(col, idx, "down")} title="Move down" className={ctl}><ArrowDown className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => moveCard(col, idx, "across")} title="Send to the other column" className={ctl}><ArrowLeftRight className="h-3.5 w-3.5" /></button>
        </div>
        {inner ?? (
          <div className="rounded-xl border border-dashed border-hairline px-3.5 py-2.5 text-xs text-slate-400">
            {CARD_LABEL[id] ?? id} — nothing to show for this customer right now; it appears here when there is.
          </div>
        )}
      </div>
    );
  }

  return (
    // DISPATCHER-FIRST-01 (Seth 2026-10-01: "Pegasus snapshot, watch for,
    // people — should maybe be on the left, or at least more towards the
    // top… if I'm a dispatcher I might not know who I might be talking to").
    // The overview reads like a call: LEFT = who's on the line + what to
    // know before you speak + the waiting work; RIGHT = the property. The
    // live-dispatch banner goes FULL-WIDTH above both. Columns stay even
    // (his Sept 1 rule) — and since C360-CUSTOM-01, the card ORDER is each
    // dispatcher's own (standard layout until they customize).
    <div>
      {/* ATTACH-01: live/just-ended unmatched call + this 360 open = offer to
          put the number (and any AI-heard email) on the account in one tap. */}
      <AttachFromCallCard custno={custno} />

      {bannerRow && bannerKind && (
        <div className="mb-5">
          <AdaptiveDispatchBanner
            row={bannerRow}
            kind={bannerKind}
            today={today}
            onPeek={setPeekDispatch}
          />
        </div>
      )}
      <DispatchPeek
        dispatchId={peekDispatch}
        onClose={() => setPeekDispatch(null)}
      />

      {/* C360-CUSTOM-01: quiet on read; a plain bar while arranging. */}
      <div className={cn("mb-2 flex items-center justify-end gap-2", customizing && "rounded-xl border border-brand-200 bg-brand-50/60 px-3 py-2")}>
        {customizing ? (
          <>
            <span className="min-w-0 flex-1 truncate text-xs font-semibold text-brand-800">
              Arranging YOUR overview — only your login sees this order.
            </span>
            <button
              type="button"
              onClick={() => saveLayout.mutate(null)}
              disabled={saveLayout.isPending || !saved}
              title="Back to the standard layout everyone starts with"
              className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-[11.5px] font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40"
            >
              <RotateCcw className="h-3 w-3" /> Reset to standard
            </button>
            <button
              type="button"
              onClick={() => setCustomizing(false)}
              className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2.5 py-1 text-[11.5px] font-semibold text-white transition-colors hover:bg-brand-800"
            >
              <Check className="h-3 w-3" /> Done
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setCustomizing(true)}
            title="Rearrange these cards — your login keeps its own order"
            className="inline-flex items-center gap-1 text-[11.5px] font-medium text-slate-300 transition-colors hover:text-brand-700"
          >
            <SlidersHorizontal className="h-3 w-3" /> Customize layout
          </button>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="min-w-0 space-y-4">
          {layout.left.map((id, i) => renderSlot(id, "left", i))}
        </div>
        <div className="min-w-0 space-y-4">
          {layout.right.map((id, i) => renderSlot(id, "right", i))}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// C360-BOARD-01 — AT THIS ADDRESS: the property photo with the systems that
// live behind that front door (model · room · year · warranty), straight off
// the same classified equipment view the Equipment tab renders. PROP-LTV-01
// adds what THIS property has billed lifetime (multi-property accounts only —
// on a single property it would just repeat the header's account number).
// ----------------------------------------------------------------------------
function AtThisAddressCard({ custno, loc, onOpenEquipment, multiProperty, customer }: { custno: string; loc: LocationRow | null; onOpenEquipment?: () => void; multiProperty?: boolean; customer?: CustomerWithLocations }) {
  const { data: equip } = useCustomerEquipment(custno, loc?.locno);
  const aiOverrides = useAiEquipmentOverrides(equip ?? []).data;
  const view = equip ? buildEquipmentView(equip, aiOverrides) : null;
  const units = [...(view?.systems ?? []), ...(view?.auxiliary ?? [])];
  // 34i82 (Seth: "At this address and property timeline are kind of showing
  // the same info"): this card is the house's STATE — one chip per system
  // that is in the house today (what · year · warranty dot) — and the
  // Property Timeline below is its STORY (when, which model, what it
  // replaced). Accessories fold into one count; the models live on the chip
  // hover, the Equipment tab and the timeline.
  // A unit the classifier could not name ("Equipment · 2010") is not a chip —
  // it folds into the count with the accessories (the Equipment tab lists it).
  // EQUIP-DEEPLINK (34i84): a system chip opens THAT unit on the Equipment tab
  // (?unit=counter) — the same landing the Property Timeline uses.
  const [, setSearchParams] = useSearchParams();
  const openUnit = (counter: string) =>
    setSearchParams((prev) => { prev.set("tab", "equipment"); prev.set("unit", counter); return prev; });
  const systems = (view?.systems ?? []).filter((e) => e.tier !== "other");
  const accessories = [...(view?.auxiliary ?? []), ...(view?.systems ?? []).filter((e) => e.tier === "other")];
  const accessoriesOnly = accessories.every((e) => e.tier === "auxiliary");
  const today = todayISO();
  const { data: lifetimes } = useLocationLifetimes(multiProperty ? custno : undefined);
  const lt = loc?.locno ? lifetimes?.get(loc.locno) : undefined;
  // BILL-TO-VIS-01 (Seth 2026-10-01: "having a hard time when looking at R&D
  // where the address and billing address is (if different)") — the old
  // rail's billing flip retired with the rail, which left the ESC bill-to
  // invisible. When the account bills somewhere OTHER than the property
  // you're viewing, say so right on the address card (R&D: every invoice
  // goes to 3300 Corporate, whichever property the work was at).
  const norm = (s?: string | null) => (s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const billsElsewhere = !!customer?.add1 && !!loc?.add1 && norm(customer.add1) !== norm(loc.add1);
  const billTo = billsElsewhere && customer
    ? [customer.add1, customer.add2, customer.city, customer.state ? `${customer.state} ${customer.zip ?? ""}`.trim() : null].filter(Boolean).join(", ")
    : null;
  const mapsUrl = loc?.add1
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([loc.add1, loc.city, loc.state, loc.zip].filter(Boolean).join(", "))}`
    : null;
  return (
    <Card className="overflow-hidden">
      {loc && (
        /* HDR-PHOTO-01 → HDR-PHOTO-03 (Seth 2026-10-09: "still showing a
           square box with a ton of empty space to the right"). The first
           fix (aspect-[16/10] + max-h) hit a CSS trap: on an auto-width box,
           a max-height TRANSFERS back through the aspect ratio and SHRINKS
           the width — in this ~720px column the tile collapsed to a 384px
           box with dead card to its right. `w-full` makes the width definite
           so the cap only trims height: full-bleed photo at every width,
           true 16:10 in narrow columns, gently letterboxed (~2.2:1) when the
           column is very wide — never a sliver, never a shrunken box. */
        <div className="relative w-full aspect-[16/10] max-h-80">
          <StreetViewThumb address={loc} className="absolute inset-0" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-2.5">
            {/* HDR-POLISH-01 (Seth: "on the google map street view it's
                missing the city"): the photo's label carries the city now,
                and right-click / the hover icon copies the FULL address. */}
            <Copyable
              text={[loc.add1, loc.city, [loc.state, loc.zip].filter(Boolean).join(" ")].filter((s) => s && String(s).trim()).join(", ")}
              label="Copy address"
              className="min-w-0"
              iconClassName="text-white/60 hover:bg-white/15 hover:text-white"
            >
              <span className="min-w-0 truncate text-[13px] font-bold text-white drop-shadow-sm">
                {loc.add1}
                {loc.city ? <span className="font-semibold text-white/80">{` · ${loc.city}`}</span> : null}
              </span>
            </Copyable>
            {mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                className="shrink-0 rounded-full bg-white/90 px-2 py-0.5 text-[10.5px] font-bold text-slate-700 transition-colors hover:bg-white">
                Map
              </a>
            )}
          </div>
        </div>
      )}
      <div className="p-3.5">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.11em] text-slate-400">At This Address</span>
          {onOpenEquipment && units.length > 0 && (
            <button type="button" onClick={onOpenEquipment}
              className="inline-flex items-center gap-0.5 text-[11.5px] font-semibold text-brand-600 transition-colors hover:text-brand-700">
              All equipment ({units.length}) <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {units.length === 0 ? (
          <p className="text-xs text-slate-400">No equipment on record here yet.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {systems.map((e) => {
              const wDate = e.warrantyParts ?? e.warrantyLabor ?? e.warrantyEffective ?? null;
              const wLive = !!wDate && wDate >= today;
              const year = installDateLabel(e.install_date, "year");
              const { Icon, ink } = equipStyle(e.category);
              const tip = [e.mfg, e.model, e.equip_loc, installDateLabel(e.install_date) ? `installed ${installDateLabel(e.install_date)}` : "install date unknown", wLive ? `warranty to ${new Date(wDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" })}` : null].filter(Boolean).join(" · ");
              return (
                <button
                  key={e.counter}
                  type="button"
                  onClick={() => openUnit(e.counter)}
                  title={tip}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-hairline bg-white px-2 py-1 text-left text-[12px] font-semibold text-slate-700 shadow-e1 transition-colors hover:border-brand-300 hover:bg-brand-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
                >
                  <Icon className={cn("h-3.5 w-3.5 shrink-0", ink)} strokeWidth={2} />
                  <span className="truncate">{e.categoryLabel}</span>
                  {year && <span className="font-medium tabular-nums text-slate-400">{year}</span>}
                  {wLive && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" aria-label="under warranty" />}
                </button>
              );
            })}
            {accessories.length > 0 && (
              <button
                type="button"
                onClick={onOpenEquipment}
                title={accessories.map((a) => a.categoryLabel).join(" · ")}
                className="inline-flex items-center rounded-lg border border-dashed border-slate-300 px-2 py-1 text-[12px] font-semibold text-slate-500 transition-colors hover:border-brand-300 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
              >
                +{accessories.length} {accessoriesOnly ? (accessories.length === 1 ? "accessory" : "accessories") : "more"}
              </button>
            )}
          </div>
        )}
        {/* BILL-TO-VIS-01 → HDR-HERO-01 (Seth: "the bills to needs to be
            bigger… very small and easy to miss"): a proper amber callout now
            — bordered, iconed, readable at a glance. Money going to the
            wrong address is exactly the mistake this exists to prevent. */}
        {billTo && (
          <div
            className="mt-2.5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 leading-snug text-amber-900"
            title="ESC bill-to on the account — invoices go here, whichever property the work was at"
          >
            <Receipt className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="min-w-0 text-[12.5px]">
              <span className="mr-1.5 inline-block translate-y-[-1px] rounded bg-amber-400/90 px-1.5 py-px text-[10px] font-extrabold uppercase tracking-wide text-amber-950">Bills to</span>
              <span className="font-semibold">{billTo}</span>
            </div>
          </div>
        )}
        {/* PROP-LTV-01: THIS property's lifetime (the header keeps the
            account-wide number — ServiceTitan-style: location value here,
            customer rollup up top). */}
        {lt && lt.total > 0 && (
          <div className="mt-2.5 border-t border-slate-100 pt-2 text-[11.5px] text-slate-500">
            <span className="font-bold tabular-nums text-emerald-700">
              {lt.total.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
            </span>{" "}
            billed at this property
            <span className="text-slate-400">
              {" "}· {lt.invoices} invoice{lt.invoices === 1 ? "" : "s"}
              {lt.firstYear ? ` · since ${lt.firstYear}` : ""}
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}

/** C360-BOARD-01 — GOOD TO KNOW: the rail's Watch-outs block, framed as its
 *  own card (gate codes, dogs, call-ahead, DNS — with the same editor). */
function GoodToKnowRailCard({ customer, locno }: { customer: CustomerWithLocations; locno: string | null }) {
  const displayName = displayCustomerName(null, customer.fullname); // RECORD-READ-MODEL: the correction is already in fullname
  const { data: statusFlag } = useCustomerStatusFlag(customer.custno);
  const dnsActive = isDoNotService(statusFlag, customer.credit_rating);
  const dnsFromEsc = !statusFlag?.do_not_service && dnsActive;
  const pegasusInactive = statusFlag?.inactive === true;
  return (
    <Card className="overflow-hidden">
      <WatchOutsBlock
        customerName={displayName}
        custno={customer.custno}
        locno={locno}
        dns={dnsActive}
        dnsReason={statusFlag?.dns_reason ?? (dnsFromEsc && customer.credit_rating ? `ESC credit rating ${customer.credit_rating}` : null)}
        inactiveReason={pegasusInactive && !customer.customerinactive ? (statusFlag?.reason ?? "") : undefined}
      />
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Right-column context cards (target layout) — live summaries + SOON holders.
// ----------------------------------------------------------------------------

/** Per-card identity color (DESIGN-SPRINT 3, Seth: "more modern, gradient,
 *  clean") — a tinted icon chip + a whisper of the same hue washing across
 *  the header. One glance tells you which box you're in. */
export type CardTone = "brand" | "sky" | "violet" | "emerald" | "amber" | "slate" | "rose";

/** 360-AUDIT #7: a fetch failure must NEVER read as "customer has none". */
function CardError({ what }: { what: string }) {
  return (
    <p className="rounded-md bg-rose-50/60 px-2.5 py-2 text-sm font-medium text-rose-700">
      Couldn&apos;t load {what} — refresh to retry. (This is a loading problem, not &ldquo;none on file&rdquo;.)
    </p>
  );
}

export const CARD_TONES: Record<
  CardTone,
  { chip: string; wash: string }
> = {
  brand: { chip: "bg-brand-50 text-brand-600", wash: "from-brand-50/70" },
  sky: { chip: "bg-sky-50 text-sky-600", wash: "from-sky-50/70" },
  violet: { chip: "bg-violet-50 text-violet-600", wash: "from-violet-50/70" },
  emerald: { chip: "bg-emerald-50 text-emerald-600", wash: "from-emerald-50/70" },
  amber: { chip: "bg-amber-50 text-amber-600", wash: "from-amber-50/70" },
  slate: { chip: "bg-slate-100 text-slate-500", wash: "from-slate-50/90" },
  rose: { chip: "bg-rose-50 text-rose-600", wash: "from-rose-50/70" },
};

function SectionCard({
  icon,
  title,
  tone: _tone,
  action,
  chip,
  children,
}: {
  icon: LucideIcon;
  title: string;
  /** Kept for callers; the header no longer tints (DESIGN-CALM-01 Wave 2 — one card header everywhere). */
  tone?: CardTone;
  action?: { label: string; onClick: () => void };
  /** Small scope/status chip rendered beside the title (e.g. "All properties"). */
  chip?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className="overflow-hidden">
      <CardHeader icon={icon} title={title} chip={chip} action={action ? { ...action, arrow: true } : undefined} />
      <div className="px-4 py-3">{children}</div>
    </Card>
  );
}

/**
 * LEAD-SOURCE-01 — where the customer came from. Original (earliest invoice's
 * sales-sort) is the locked anchor; latest shows if it drifted; a Pegasus
 * override corrects a wrong one. Options come from the managed `lead_source`
 * picklist (imported from ESC, admin can add new). Attribution lives at the
 * BOTTOM — it matters to the business, not the dispatcher mid-call.
 */
function LeadSourceCard({ custno }: { custno: string }) {
  const { data: opts } = useLeadSourceOptions();
  const { data: ls } = useCustomerLeadSource(custno);
  const setOv = useSetCustomerLeadOverride();
  const addOpt = useAddLeadSourceOption();
  const [editing, setEditingState] = useState(false);
  const edit = useEditCustomer(); // EDIT-SUITE-01: Correct / Set source opens the suite at Name & Account
  const setEditing = (v: boolean) => (v && edit.available ? edit.open("account") : setEditingState(v));
  const [adding, setAdding] = useState(false);
  const [nc, setNc] = useState("");
  const [nl, setNl] = useState("");

  const byCode = new Map((opts ?? []).map((o) => [o.code, o.label]));
  const lab = (c: string | null) => (c ? (byCode.get(c) ?? c) : null);

  if (!ls) return null;
  // HONESTY FIX (Seth 2026-06-10, "almost every one says tech-generated"):
  // a code that first appears YEARS after the first invoice is not where the
  // customer came from — it's just the first invoice someone coded. Headline
  // = the override, or the FIRST invoice's code when it's a real source
  // (not CUR "existing customer" / OTHER). Anything else is shown only as a
  // clearly-labeled hint below. Stated beats derived; derived must say so.
  const originalIsReal =
    !!ls.originalCode && !["CUR", "OTHER"].includes(ls.originalCode);
  const primary = ls.overrideCode ?? (originalIsReal ? ls.originalCode : null);
  const primaryLabel = lab(primary);
  const sinceYear = ls.originalDate ? new Date(ls.originalDate).getFullYear() : null;
  const hintCode = !primary && ls.specificCode ? ls.specificCode : null;
  const hintYear = ls.specificDate ? new Date(ls.specificDate).getFullYear() : null;
  const latestDiffers = !!primary && ls.latestCode && ls.latestCode !== primary;

  return (
    <SectionCard icon={Megaphone} title="Lead Source" tone="amber">
      {!editing ? (
        <div className="space-y-1">
          {primaryLabel ? (
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-semibold text-slate-900">
                {primaryLabel}
              </span>
              {sinceYear && (
                <span className="text-xs text-slate-400">since {sinceYear}</span>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-500">
              Not recorded
              {sinceYear ? (
                <span className="text-slate-400"> · customer since {sinceYear}</span>
              ) : null}
            </p>
          )}
          {ls.overrideCode && (
            <p className="text-xs text-slate-400">Corrected in Pegasus.</p>
          )}
          {hintCode && (
            <p className="text-xs text-slate-400">
              First source on a dispatch/invoice: {lab(hintCode)}
              {hintYear ? ` (${hintYear})` : ""} — may not be how they found us.
            </p>
          )}
          {latestDiffers && (
            <p className="text-xs text-slate-500">
              Most recent: {lab(ls.latestCode)}
            </p>
          )}
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1 pt-1 text-xs font-medium text-brand-600 transition-colors hover:text-brand-700"
          >
            <Pencil className="h-3 w-3" /> {primaryLabel ? "Correct" : "Set source"}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Explainer lives in EDIT mode only (facelift pass 1: the card
              carried a permanent paragraph of fine print — noise on read). */}
          <p className="text-xs leading-snug text-slate-400">
            The original source is locked from the customer&apos;s earliest
            dispatch — correct it here only if it&apos;s wrong.
          </p>
          <select
            value={primary ?? ""}
            onChange={(e) =>
              // 360-AUDIT wave 2 (E): a failed save silently snapped back.
              setOv.mutate({ custno, code: e.target.value || null }, { onError: onErrToast("Couldn't save the lead source") })
            }
            className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
          >
            <option value="">— not set —</option>
            {(opts ?? [])
              .filter((o) => o.active)
              .map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
          </select>
          {!adding ? (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 transition-colors hover:text-brand-700"
            >
              <Plus className="h-3 w-3" /> Add a new source
            </button>
          ) : (
            <div className="flex items-end gap-1.5">
              <input
                value={nc}
                onChange={(e) => setNc(e.target.value)}
                placeholder="CODE"
                className="w-16 rounded-md border border-hairline px-1.5 py-1 text-xs uppercase"
              />
              <input
                value={nl}
                onChange={(e) => setNl(e.target.value)}
                placeholder="Label"
                className="min-w-0 flex-1 rounded-md border border-hairline px-1.5 py-1 text-xs"
              />
              <button
                type="button"
                disabled={!nc.trim() || !nl.trim() || addOpt.isPending}
                onClick={() =>
                  addOpt.mutate(
                    { code: nc, label: nl },
                    {
                      onSuccess: () => {
                        setAdding(false);
                        setNc("");
                        setNl("");
                      },
                      onError: onErrToast("Couldn't add the lead source"),
                    },
                  )
                }
                className="rounded-md bg-brand-700 px-2 py-1 text-xs font-semibold text-white disabled:bg-brand-300"
              >
                Add
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="block pt-1 text-xs font-medium text-slate-500 transition-colors hover:text-slate-700"
          >
            Done
          </button>
        </div>
      )}
    </SectionCard>
  );
}

/**
 * Messages & touchpoints (COMMS-02, LIVE) — last touch + count from the one
 * thread; "Open thread" jumps to the Messages tab. Auto-arrival of texts and
 * emails rides the Podium/M365 hookups (COMMS-03); calls log today.
 */
export function MessagesSummaryCard({ custno }: { custno: string }) {
  const { data: messagesData, isLoading } = useMessages(custno);
  const messages = messagesData ?? [];
  const [searchParams, setSearchParams] = useSearchParams();
  const openThread = () => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "messages");
    setSearchParams(next, { replace: false });
  };
  // 360-AUDIT #2: useMessages orders created_at DESC — newest is [0].
  const last = messages[0];

  return (
    <SectionCard
      icon={MessageSquare}
      title="Messages & Touchpoints"
      tone="brand"
      action={{ label: "Open Thread", onClick: openThread }}
    >
      {isLoading ? (
        <CardLoading lines={2} />
      ) : last ? (
        <div className="text-sm text-slate-700">
          <p className="line-clamp-2">
            <span className="font-medium">
              {last.channel === "note"
                ? "Internal note"
                : `${last.direction === "inbound" ? "From them" : "To them"} · ${
                    { sms: "text", email: "email", chat: "chat", web: "web", call: "call", note: "note" }[last.channel]
                  }`}
              :
            </span>{" "}
            {last.body}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            {format(parseISO(last.created_at), "MMM d, yyyy")} ·{" "}
            {messages.length} on the thread
          </p>
        </div>
      ) : (
        // DESIGN-CALM-01: one line, not a paragraph.
        <p className="text-sm text-slate-500">No messages yet.</p>
      )}
    </SectionCard>
  );
}

/**
 * Loading shimmer for a summary card. A card must NEVER show its empty-state
 * copy ("No equipment on record", "No active agreement") while its query is
 * still in flight — a dispatcher glancing mid-call reads that as the truth
 * and quotes the wrong thing. Skeleton until settled. (AUDIT-2026-06-09 A4.)
 */
function CardLoading({ lines = 2 }: { lines?: number }) {
  return (
    <div className="animate-pulse space-y-2" aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-3 rounded bg-slate-100"
          style={{ width: `${88 - i * 24}%` }}
        />
      ))}
    </div>
  );
}

/**
 * AiSnapshotCard (FACELIFT-360 pass 1) — the AI summary, promoted from the
 * bottom of the Notes tab to the top of the context rail. Compact: clamped to
 * ~5 lines with Show more. Self-hides when there's no location or no summary;
 * on error it stays silent here (the Notes tab carries the full retry card).
 * Cost-safe: summarize-location caches by source-content hash server-side, so
 * this only spends AI credits when the underlying notes/history changed.
 *
 * SNAP-FIX-01 (Seth 2026-10-01): "Fix something?" — typing to the AI. The
 * office writes plain-English corrections ("this is a residential address";
 * "'Jeremy only' means he's the requested tech"); they're saved per property
 * and the summarizer treats them as the truth on every regeneration from
 * then on. Saving regenerates the snapshot right away.
 */
function AiSnapshotCard({
  custno,
  locno,
}: {
  custno: string;
  locno?: string | null;
}) {
  const { data, isLoading, isFetching, refetch } = useLocationNotesSummary(
    custno,
    locno ?? undefined,
  );
  const [expanded, setExpanded] = useState(false);
  const { data: correction } = useSnapshotNote(custno, locno);
  const saveFix = useSaveSnapshotNote();
  const [fixing, setFixing] = useState(false);
  const editDoor = useEditCustomer(); // EDIT-SUITE-01: Fix something? opens the suite at Notes & Snapshot
  const [fixText, setFixText] = useState("");
  if (!locno) return null;
  if (isLoading) {
    return (
      <SectionCard icon={Sparkles} title="Pegasus Snapshot" tone="brand">
        <CardLoading lines={3} />
      </SectionCard>
    );
  }
  if (!data?.summary) return null;
  // READABILITY (Seth 2026-08-21: "not have to expand the AI summary
  // without it looking too large"): typical summaries (2-4 short paragraphs)
  // now show IN FULL — only genuinely long ones clamp, and at 12 lines.
  const long = data.summary.length > 900;
  return (
    <SectionCard icon={Sparkles} title="Pegasus Snapshot" tone="brand">
      <div
        className={cn(
          "whitespace-pre-line text-sm leading-relaxed text-slate-700",
          !expanded && long && "line-clamp-[12]",
        )}
      >
        {data.summary}
      </div>
      {long && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 text-[13px] font-medium text-brand-600 transition-colors hover:text-brand-700"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
      <div className="mt-2 flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
        <span className="text-xs text-slate-400">
          ✨ AI-written from notes &amp; history — double-check anything critical.
        </span>
        <span className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={() => {
              if (editDoor.available) { editDoor.open("notes"); return; }
              setFixText(correction?.body ?? "");
              setFixing((v) => !v);
            }}
            title="Tell the AI what it got wrong — your words become the standing truth for this property's snapshot"
            className={cn(
              "inline-flex items-center gap-1 text-xs font-medium transition-colors",
              correction?.body
                ? "text-brand-600 hover:text-brand-700"
                : "text-slate-400 hover:text-slate-600",
            )}
          >
            <Pencil className="h-3 w-3" />
            {correction?.body ? "Office correction on" : "Fix something?"}
          </button>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Re-check for changes (regenerates only if notes or history changed)"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 transition-colors hover:text-slate-600 disabled:cursor-wait"
          >
            <RefreshCw className={cn("h-3 w-3", isFetching && "animate-spin")} />
            {formatSnapshotStamp(data.generated_at)}
          </button>
        </span>
      </div>
      {fixing && (
        <div className="mt-2 rounded-lg border border-brand-200 bg-brand-50/60 p-2.5">
          <p className="mb-1.5 text-xs leading-relaxed text-brand-800">
            Type corrections in plain English — the AI treats what you write
            here as the truth about this property and rewrites the snapshot
            around it. It stays in force for every future rewrite.
          </p>
          <textarea
            value={fixText}
            onChange={(e) => setFixText(e.target.value)}
            rows={3}
            autoFocus
            placeholder={`e.g. "This is a residential address — the account is under the company name, but this property is a home." or "Notes saying 'Jim only' mean Jim is their requested technician."`}
            className="w-full resize-y rounded-md border border-brand-200 bg-white px-2.5 py-1.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none"
          />
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <span className="text-[11px] text-slate-400">
              {correction?.body
                ? "Clearing the box and saving removes the correction."
                : "Saved per property — not per account."}
            </span>
            <span className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFixing(false)}
                className="rounded-md px-2 py-1 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saveFix.isPending}
                onClick={() =>
                  saveFix.mutate(
                    { custno, locno, body: fixText },
                    { onSuccess: () => setFixing(false), onError: onErrToast("save the correction") },
                  )
                }
                className="rounded-md bg-brand-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
              >
                {saveFix.isPending ? "Saving…" : "Save & rewrite snapshot"}
              </button>
            </span>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function formatSnapshotStamp(iso: string): string {
  try {
    return format(parseISO(iso), "MMM d");
  } catch {
    return "";
  }
}

export function EquipmentSummaryCard({
  custno,
  locno,
}: {
  custno: string;
  locno?: string | null;
}) {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useCustomerEquipment(custno, locno);
  const aiOverrides = useAiEquipmentOverrides(data ?? []).data;
  const view = data ? buildEquipmentView(data, aiOverrides) : null;
  const systems = view?.systems ?? [];
  return (
    <SectionCard
      icon={Boxes}
      title="Equipment"
      tone="sky"
      action={{
        label: systems.length > 0 ? `All ${systems.length}` : "Open",
        onClick: () => navigate(`/customers/${custno}?tab=equipment`),
      }}
    >
      {isLoading ? (
        <CardLoading lines={3} />
      ) : isError ? (
        <CardError what="equipment" />
      ) : systems.length === 0 ? (
        <p className="text-sm text-slate-500">
          No equipment on record for this {locno ? "location" : "customer"}.
        </p>
      ) : (
        <ul className="space-y-2">
          {systems.slice(0, 4).map((s) => (
            <li key={s.counter} className="flex items-baseline gap-2 text-sm">
              <span className="font-medium text-slate-800">
                {s.categoryLabel}
              </span>
              <span className="min-w-0 flex-1 truncate text-slate-500">
                {s.model ?? "—"}
              </span>
              {s.installYear && (
                <span className="text-xs tabular-nums text-slate-400">
                  ’{String(s.installYear).slice(2)}
                </span>
              )}
            </li>
          ))}
          {systems.length > 4 && (
            <li className="text-xs text-slate-400">
              + {systems.length - 4} more
            </li>
          )}
        </ul>
      )}
      {/* Builder note only on the EMPTY card (2c); the frayed Add stays —
          it marks where the action lands, which is useful on filled cards. */}
      <div className="mt-2 flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
        {/* DESIGN-CALM-01: the builder's note came off — the empty card says
            "No equipment on record" above, which is the whole message. */}
        <span />
        <FrayedAction
          kind="add"
          title="Add equipment — lands as Pegasus takes over the equipment record"
        />
      </div>
    </SectionCard>
  );
}

/**
 * MembershipSummaryCard (MEMBERSHIP-CARD-01) — rebuilt from the one-liner it
 * used to be. Answers Seth's "what KIND of plus customer is this" without
 * leaving the Overview: plan name + status, member-since year, price/term,
 * the visit ledger (X of Y with bars), autopay, and when it runs out.
 */
export function MembershipSummaryCard({ custno, multiProperty }: { custno: string; multiProperty?: boolean }) {
  const navigate = useNavigate();
  const { data: m, isLoading, isError } = useMembershipCard(custno);

  const STATUS_CHIP: Record<
    NonNullable<typeof m>["status"],
    { label: string; cls: string }
  > = {
    active: { label: "Active", cls: "bg-emerald-50 text-emerald-700" },
    expiring: { label: "Expiring", cls: "bg-amber-50 text-amber-800" },
    ended: { label: "Ended · checks owed", cls: "bg-amber-50 text-amber-800" },
    expired: { label: "Expired", cls: "bg-rose-50 text-rose-700" },
    inactive: { label: "Inactive", cls: "bg-slate-100 text-slate-500" },
  };

  const fmtMonthYear = (iso: string | null) => {
    if (!iso) return null;
    try {
      return format(parseISO(iso), "MMM yyyy");
    } catch {
      return iso;
    }
  };
  const fmtDay = (iso: string | null) => {
    if (!iso) return null;
    try {
      return format(parseISO(iso), "MMM d, yyyy");
    } catch {
      return iso;
    }
  };

  return (
    <SectionCard
      icon={ShieldCheck}
      title="Membership"
      chip={<AllPropertiesChip show={!!multiProperty} />}
      tone="violet"
      action={{
        label: "Open",
        onClick: () => navigate(`/customers/${custno}?tab=agreements`),
      }}
    >
      {isLoading ? (
        <CardLoading lines={3} />
      ) : m ? (
        <div className="space-y-2.5">
          {/* Plan identity — the headline this card never had. */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-base font-bold leading-snug text-slate-900">
                {m.planName}
              </div>
              <div className="mt-0.5 text-xs text-slate-500">
                {m.memberSinceYear && (
                  <span className="font-medium text-slate-600">
                    Member since {m.memberSinceYear}
                  </span>
                )}
                {m.memberSinceYear && m.priceAmount != null && " · "}
                {m.priceAmount != null && (
                  <span>
                    ${m.priceAmount.toFixed(2)}
                    {m.termLabel ? ` / ${m.termLabel.replace(" term", "")}` : ""}
                  </span>
                )}
              </div>
            </div>
            <span
              className={cn(
                "shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold",
                STATUS_CHIP[m.status].cls,
              )}
            >
              {STATUS_CHIP[m.status].label}
            </span>
          </div>

          {/* Plan-wide visit ledger. ESC's per-check "X of Y" numbers are
              POSITIONS in one shared visit sequence, not per-type counts —
              so the honest read is one bar + what's next. */}
          {m.planVisits && (
            <div>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm text-slate-700">Plan visits</span>
                <span className="text-xs font-semibold tabular-nums text-slate-600">
                  {m.planVisits.used} of {m.planVisits.total} used
                </span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-violet-400"
                  style={{
                    width: `${Math.min(100, Math.round((m.planVisits.used / Math.max(m.planVisits.total, 1)) * 100))}%`,
                  }}
                />
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {m.nextCheck && (
                  <span className="font-medium text-slate-600">
                    Next up: {m.nextCheck}
                  </span>
                )}
                {m.nextCheck && m.includes.length > 0 && " · "}
                {m.includes.length > 0 && (
                  <span>covers {m.includes.map((s) => s.replace(" tune-up", "")).join(" + ")}</span>
                )}
              </div>
            </div>
          )}

          {/* Pegasus-plan extras */}
          {(m.autopay || m.secondSystem) && (
            <div className="flex flex-wrap gap-1.5">
              {m.autopay && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                  Autopay on
                </span>
              )}
              {m.secondSystem && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                  2nd system
                </span>
              )}
            </div>
          )}

          {/* Horizon line — dates a dispatcher can quote. */}
          <p className="border-t border-slate-100 pt-2 text-xs leading-relaxed text-slate-500">
            {m.begDate && m.expireDate
              ? `Runs ${fmtMonthYear(m.begDate)} – ${fmtMonthYear(m.expireDate)}`
              : m.expireDate
                ? `Through ${fmtMonthYear(m.expireDate)}`
                : null}
            {m.daysToExpire != null && m.daysToExpire >= 0 && (
              <span
                className={cn(
                  m.status === "expiring" && "font-semibold text-amber-700",
                )}
              >
                {" "}
                ·{" "}
                {m.daysToExpire === 0
                  ? "renews today"
                  : `renews in ${m.daysToExpire} days`}
              </span>
            )}
            {m.lastService && ` · last plan visit ${fmtDay(m.lastService)}`}
          </p>
        </div>
      ) : isError ? (
        <CardError what="the membership" />
      ) : (
        <p className="text-sm text-slate-500">
          No membership on file — sell one from the Agreements tab.
        </p>
      )}
    </SectionCard>
  );
}

/** 360-RELOOK-01: the Money tab (last on the rail). Today it carries the
 *  lifetime-revenue snapshot that used to sit on the Overview; the balance
 *  and invoices stay on Invoices. The relook pass decides what else lives
 *  here (payments on file, financing, agreement value). */
export function CustomerMoneyTab({ custno, multiProperty }: { custno: string; multiProperty?: boolean }) {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <MoneySnapshotCard custno={custno} multiProperty={multiProperty} />
    </div>
  );
}

function MoneySnapshotCard({ custno, multiProperty }: { custno: string; multiProperty?: boolean }) {
  const { data: metrics, isLoading, isError } = useCustomerMetrics(custno);
  const revenue = metrics?.lifetime_revenue_cents ?? 0;
  const dispatches = metrics?.total_dispatches ?? 0;
  // The Jennifer Mueller rule: numbers only with evidence behind them.
  const chimera = metrics?.tenure_data_quality === "chimera_suspected";
  return (
    <SectionCard icon={DollarSign} title="Money" tone="emerald" chip={<AllPropertiesChip show={!!multiProperty} />}>
      {isLoading ? (
        <CardLoading lines={2} />
      ) : chimera ? (
        <p className="text-sm text-slate-500">
          Totals held back — history may belong to a previous owner.
        </p>
      ) : revenue > 0 || dispatches > 0 ? (
        <div className="flex items-baseline gap-6">
          <div>
            <div className="text-2xl font-bold leading-none tracking-tight text-slate-900">
              {formatCentsAsHuman(revenue)}
            </div>
            <div className="mt-1 text-xs text-slate-500">lifetime</div>
          </div>
          <div>
            <div className="text-2xl font-bold leading-none tracking-tight text-slate-900 tabular-nums">
              {dispatches}
            </div>
            <div className="mt-1 text-xs text-slate-500">dispatches</div>
          </div>
        </div>
      ) : isError ? (
        <CardError what="billing totals" />
      ) : (
        <p className="text-sm text-slate-500">No billing history yet.</p>
      )}
    </SectionCard>
  );
}

// ----------------------------------------------------------------------------
// Adaptive dispatch banner — the "what's happening with this customer now" card.
// ----------------------------------------------------------------------------

function AdaptiveDispatchBanner({
  row,
  kind,
  today,
  onPeek,
}: {
  row: CustomerDispatchRow;
  kind: "active" | "upcoming";
  today: string;
  onPeek: (dispatch: string) => void;
}) {
  const { timeWindow, priority } = resolveWindowAndPriority(row.window_code, row.notes);
  const time = formatTime(row.tpromtime);
  const what = extractJobPreview(row.notes, 90);
  const techName =
    row.tech_name ?? `${row.tech_first ?? ""} ${row.tech_last ?? ""}`.trim();

  let dateLabel = row.appointment_date ?? "";
  try {
    if (row.appointment_date) {
      dateLabel = format(parseISO(row.appointment_date), "EEE, MMM d");
    }
  } catch {
    /* keep raw */
  }
  const isToday = row.appointment_date === today;

  const label =
    kind === "active"
      ? "Active now"
      : isToday
        ? "Scheduled today"
        : "Next visit";

  const tone =
    kind === "active"
      ? {
          ring: "ring-emerald-200",
          bg: "bg-emerald-50/50",
          accent: "text-emerald-700",
          Icon: Activity,
        }
      : {
          ring: "ring-brand-200",
          bg: "bg-brand-50/50",
          accent: "text-brand-700",
          Icon: CalendarClock,
        };

  // FLOW-01: peek in place — the dispatcher stays on the customer page.
  function open() {
    onPeek(row.dispatch);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      className={cn(
        "group block w-full cursor-pointer rounded-xl p-4 text-left ring-1 ring-inset transition-all duration-200 ease-out-quart",
        "hover:shadow-e2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50",
        tone.bg,
        tone.ring,
      )}
    >
      {/* Label row */}
      <div className="mb-2 flex items-center justify-between gap-2">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider",
            tone.accent,
          )}
        >
          <tone.Icon className="h-3.5 w-3.5" />
          {label}
          {kind === "active" && (
            <span className="relative ml-0.5 flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
          )}
        </span>
        <StatusBadge status={row.status} variant="pill" />
      </div>

      {/* Foregrounded window + priority badges (the at-a-glance signal for a
          live/upcoming job), then date + time. */}
      <div className="flex flex-wrap items-center gap-2">
        {timeWindow && <WindowBadge code={timeWindow} />}
        {priority && <WindowBadge code={priority} />}
        <span className="text-sm font-medium text-slate-700">{dateLabel}</span>
        {time && (
          <span className="inline-flex items-center gap-1 text-sm text-slate-500">
            <Clock className="h-3.5 w-3.5" />
            {time}
          </span>
        )}
      </div>

      {/* Job preview + tech */}
      {what && what !== "—" && (
        <div className="mt-2 text-sm text-slate-700">{what}</div>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {techName ? (
            <>
              <TechAvatar empno={row.tech_empno} name={techName} size="sm" />
              <TechLink empno={row.tech_empno} name={techName} className="text-sm" />
            </>
          ) : (
            <span className="text-xs italic text-slate-500">Unassigned</span>
          )}
        </div>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 opacity-0 transition-opacity group-hover:opacity-100">
          Open dispatch
          <ChevronRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </div>
  );
}

export function RecentServiceRow({
  row,
  headline,
  priorOwner,
  onPeek,
}: {
  row: CustomerDispatchRow;
  headline: VisitHeadline;
  priorOwner?: string | null;
  onPeek: (dispatch: string) => void;
}) {
  const win = resolveWindow(row.window_code, row.notes);
  const what = headline.text;
  const techName =
    row.tech_name ?? `${row.tech_first ?? ""} ${row.tech_last ?? ""}`.trim();
  let dateLabel = "—";
  try {
    dateLabel = format(parseISO(row.appointment_date), "MMM d, yyyy");
  } catch {
    dateLabel = row.appointment_date;
  }

  // FLOW-01 (Seth): stay ON the customer page — the dispatch peeks in from
  // the right; the full page is a deliberate drill-down inside the peek.
  function open() {
    if (row.peg) return; // Pegasus-native visit — no ESC dispatch to peek (yet)
    onPeek(row.dispatch);
  }

  return (
    <li
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      className="group flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-brand-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40"
    >
      <div className="w-24 flex-shrink-0">
        <div className="text-[13px] font-medium text-slate-700">{dateLabel}</div>
        {row.peg && <span className="mt-0.5 inline-block rounded bg-sky-100 px-1 py-0.5 text-[9px] font-bold text-sky-700">Pegasus</span>}
        <div className="mt-1">
          {win.value ? (
            <WindowBadge code={win.value} />
          ) : (
            <span className="text-xs text-slate-400">—</span>
          )}
        </div>
        {priorOwner && (
          <div
            className="mt-1 inline-flex items-center rounded bg-slate-100 px-1 py-px text-[10px] font-medium text-slate-500"
            title={`This service was under the previous owner, ${priorOwner}`}
          >
            prev. owner
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        {/* The WORK is the headline of a service row (value-first) — the AI
            line when it exists; the raw booking note rides in the hover and
            the peek (SERVICE-ONELINE-01). */}
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="min-w-0 truncate text-sm font-medium text-slate-800" title={headline.raw || undefined}>{what}</span>
          {headline.amount != null && (
            <span className="shrink-0 font-mono text-[11.5px] tabular-nums text-slate-400" title="Invoice total for this visit">
              {headline.amount.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-slate-500" onClick={(e) => e.stopPropagation()}>
          {techName ? <TechLink empno={row.tech_empno} name={techName} /> : "—"}
        </div>
      </div>

      <StatusBadge status={row.status} variant="pill" />
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-brand-500" />
    </li>
  );
}

// ----------------------------------------------------------------------------
// RecordGroup (STRIP+A, Seth 2026-08-21) — Files, Lead Source and the
// Property Timeline are LOOK-UP facts, not glance facts. One compact card
// summarizes all three; clicking a segment opens the full existing card right
// beneath it. Three card headers become one — the page stops paying rent on
// record-keeping.
// ----------------------------------------------------------------------------
function RecordGroup({ custno, locno }: { custno: string; locno?: string | null }) {
  const files = useCustomerFiles(custno).data ?? [];
  const inScope = locno ? files.filter((f) => !f.locno || f.locno === locno) : files;
  const photoCount = inScope.filter((f) => f.kind === "photo").length;
  const docCount = inScope.length - photoCount;
  const { data: ls } = useCustomerLeadSource(custno);
  const { data: opts } = useLeadSourceOptions();
  // Same honesty rule as the full card: override wins; the original code only
  // counts when it's a real source (not CUR/OTHER).
  const originalIsReal = !!ls?.originalCode && !["CUR", "OTHER"].includes(ls.originalCode);
  const leadCode = ls?.overrideCode ?? (originalIsReal ? (ls?.originalCode ?? null) : null);
  const leadLabel = leadCode ? (opts?.find((o) => o.code === leadCode)?.label ?? leadCode) : null;
  // 360-HD (Seth 2026-09-01: "needs to be more obvious that you can cycle
  // through those"): a REAL segmented control — three equal segments on a
  // track, one always open (files by default), content right below. Cycling
  // is self-evident because switching visibly swaps the panel.
  const [open, setOpen] = useState<"files" | "lead">("files");

  return (
    <>
      <SectionCard icon={FolderOpen} title="Records" tone="slate">
        <div className="flex gap-0.5 rounded-full bg-slate-100 p-0.5">
          {([
            ["files", "Files & Photos", String(photoCount + docCount)],
            ["lead", "Lead Source", null],
          ] as const).map(([k, label, badge]) => (
            <button
              key={k}
              type="button"
              onClick={() => setOpen(k)}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-full px-2 py-1.5 text-[12.5px] font-bold transition-all",
                open === k
                  ? "bg-white text-slate-900 shadow-[0_1px_4px_rgba(0,0,0,0.14)]"
                  : "text-slate-500 hover:text-slate-700",
              )}
            >
              {label}
              {badge != null && (
                <span className={cn(
                  "rounded-full px-1.5 py-px text-[10.5px] font-bold tabular-nums",
                  open === k ? "bg-slate-100 text-slate-600" : "bg-slate-200/70 text-slate-500",
                )}>
                  {badge}
                </span>
              )}
            </button>
          ))}
        </div>
        {leadLabel && open !== "lead" && (
          <p className="mt-1.5 px-1 text-[11px] text-slate-400">Lead source on file: {leadLabel}</p>
        )}
      </SectionCard>
      {open === "files" && <CustomerFiles custno={custno} locno={locno} />}
      {open === "lead" && <LeadSourceCard custno={custno} />}
    </>
  );
}
